import { useEffect, useMemo, useState } from "react";
import { reduceObj } from "../common/services/Utilities";
import { KeyValue, Query } from "../interfaces";

export default function useGetTableCheckboxState(
  appState: Query,
  clearValues?: boolean,
  setClearValues?: (val: boolean) => void,
  newCheckboxState?: KeyValue,
  currentCheckboxes?: string[],
) {
  const newAppState = useMemo(() => {
    return { ...appState.filters, ...appState.search };
  }, [appState]);
  const [previousAppState, setPreviousAppState] = useState(newAppState);
  const [allCheckboxes, setAllCheckboxes] = useState<KeyValue>({});
  const [selectedHeader, setSelectedHeader] = useState<boolean | string>("");
  const [selectedCheckBox, setSelectedCheckBox] = useState("");
  const [previousCheckboxKeys, setPreviousCheckboxKeys] = useState<string[]>();
  //properties used in the table header to display the checkbox state
  const checkboxHeaderProps = {
    checkboxClick: setSelectedHeader,
    checked:
      Object.keys(allCheckboxes).length > 0 &&
      Object.keys(allCheckboxes).every(
        (val: any) => allCheckboxes[val] === true,
      ),
  };

  const visibleCheckboxes = currentCheckboxes
    ? currentCheckboxes
    : appState.ui.visibleTableCheckboxes;
  useEffect(() => {
    if (
      newCheckboxState &&
      Object.keys(newCheckboxState).length > 0 &&
      JSON.stringify(newCheckboxState) !== JSON.stringify(allCheckboxes)
    ) {
      setAllCheckboxes(newCheckboxState);
    }
  }, [newCheckboxState, allCheckboxes]);
  //properties used in the table body to display the checkbox state
  const checkboxTableBodyProps = {
    checkboxClick: setSelectedCheckBox,
    allCheckboxes: allCheckboxes,
  };

  const allTrueVals = useMemo(() => {
    return (
      visibleCheckboxes && {
        ...allCheckboxes,
        ...reduceObj(
          visibleCheckboxes.map((value: any) => {
            return { key: value, value: true };
          }),
        ),
      }
    );
  }, [visibleCheckboxes, allCheckboxes]);
  const allFalseVals = useMemo(() => {
    return (
      visibleCheckboxes && {
        ...allCheckboxes,
        ...reduceObj(
          visibleCheckboxes.map((value: any) => {
            return { key: value, value: false };
          }),
        ),
      }
    );
  }, [visibleCheckboxes, allCheckboxes]);

  useEffect(() => {
    if (JSON.stringify(previousAppState) !== JSON.stringify(newAppState)) {
      setPreviousAppState(newAppState);
      setSelectedHeader("");
      setSelectedCheckBox("");
    }
  }, [previousAppState, newAppState, allFalseVals]);
  //used to reset checkbox state for all to false
  useEffect(() => {
    if (clearValues && setClearValues) {
      let falsifyVals = { ...allCheckboxes };

      Object.keys(falsifyVals).forEach(val => (falsifyVals[val] = false));
      setSelectedHeader("");
      setSelectedCheckBox("");
      const newCheckboxes =
        appState.ui.pgcStep === "step1" ? falsifyVals : allFalseVals;
      allCheckboxes && setAllCheckboxes(newCheckboxes);
      setClearValues(false);
    }
  }, [
    allFalseVals,
    clearValues,
    setAllCheckboxes,
    setClearValues,
    appState.ui.pgcStep,
    allCheckboxes,
  ]);

  useEffect(() => {
    if (
      JSON.stringify(visibleCheckboxes) !== JSON.stringify(previousCheckboxKeys)
    ) {
      const newObj = Object.keys(allCheckboxes)
        .filter(key => allCheckboxes[key])
        .reduce((obj: any, key: any) => {
          obj[key] = allCheckboxes[key];
          return obj;
        }, {});

      const newCertsObj = visibleCheckboxes
        ? visibleCheckboxes.map((cert: string) => {
            return {
              key: cert,
              value: allCheckboxes[cert] ? true : false,
            };
          })
        : [];
      setPreviousCheckboxKeys(visibleCheckboxes);
      setAllCheckboxes({ ...newObj, ...reduceObj(newCertsObj) });
    }
  }, [
    allCheckboxes,
    visibleCheckboxes,
    previousCheckboxKeys,
    setAllCheckboxes,
  ]);

  //sets the checkbox states based on selections from the table
  useEffect(() => {
    if (selectedCheckBox && !allCheckboxes[selectedCheckBox]) {
      setAllCheckboxes({ ...allCheckboxes, [selectedCheckBox]: true });
      setSelectedCheckBox("");
    } else if (selectedCheckBox && allCheckboxes[selectedCheckBox]) {
      setAllCheckboxes({ ...allCheckboxes, [selectedCheckBox]: false });
      setSelectedCheckBox("");
    } else if (selectedHeader === true) {
      setSelectedHeader("");
      setAllCheckboxes(allTrueVals);
    } else if (selectedHeader === false) {
      setSelectedHeader("");
      allFalseVals && setAllCheckboxes(allFalseVals);
    }
  }, [
    allCheckboxes,
    allFalseVals,
    allTrueVals,
    selectedCheckBox,
    selectedHeader,
    setAllCheckboxes,
  ]);

  return { header: checkboxHeaderProps, body: checkboxTableBodyProps };
}
